/**
 * Shared Animation Constants & Utilities
 *
 * Centralized animation configuration for consistent motion across the app.
 * All animations respect user's prefers-reduced-motion preference via useReducedMotion.
 *
 * Usage:
 * ```tsx
 * import { useReducedMotion } from 'framer-motion';
 * import { ANIMATIONS, getAnimationProps } from '@/lib/animations';
 *
 * const shouldReduceMotion = useReducedMotion();
 * <motion.div {...getAnimationProps(ANIMATIONS.fadeInUp, shouldReduceMotion)} />
 * ```
 */

import type { Variants, Transition } from 'framer-motion';

// ============================================================================
// TIMING CONSTANTS
// ============================================================================

/** Standard durations matching design system */
export const DURATION = {
  fast: 0.15,      // Quick micro-interactions (150ms)
  normal: 0.2,     // Standard transitions (200ms)
  slow: 0.3,       // Emphasized transitions (300ms)
} as const;

/** Standard easings using cubic-bezier */
export const EASING = {
  // Standard ease-out for most animations
  easeOut: [0.4, 0, 0.2, 1] as const,
  // Ease-in-out for symmetric transitions
  easeInOut: [0.4, 0, 0.2, 1] as const,
  // Spring-like bounce (exaggerated)
  bounce: [0.68, -0.55, 0.265, 1.55] as const,
} as const;

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

/** Sticky button animation (scale + fade from bottom) */
export const STICKY_BUTTON: Variants = {
  initial: { opacity: 0, y: 20, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 20, scale: 0.95 },
};

/** Popover content animation (scale + fade from top) */
export const POPOVER: Variants = {
  hidden: { opacity: 0, y: -8, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -8, scale: 0.96 },
};

/** Badge animation (scale + fade) */
export const BADGE: Variants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.8, transition: { duration: 0.1 } },
};

/** Table row animation (slide + fade) */
export const TABLE_ROW: Variants = {
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, x: -20 },
};

/** Height collapse animation */
export const COLLAPSE: Variants = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 },
};

// ============================================================================
// ANIMATION PRESETS (with transition)
// ============================================================================

export const ANIMATIONS = {
  stickyButton: {
    variants: STICKY_BUTTON,
    transition: { duration: DURATION.normal, ease: EASING.easeOut },
  },
  popover: {
    variants: POPOVER,
    transition: { duration: DURATION.fast, ease: EASING.easeOut },
  },
  badge: {
    variants: BADGE,
    transition: { duration: DURATION.fast, ease: EASING.easeOut },
  },
  tableRow: {
    variants: TABLE_ROW,
    transition: { duration: DURATION.normal, ease: EASING.easeOut },
  },
  collapse: {
    variants: COLLAPSE,
    transition: { duration: DURATION.normal, ease: EASING.easeOut },
  },
} as const;

// ============================================================================
// REDUCED MOTION UTILITIES
// ============================================================================

/** Empty variants for reduced motion - instant state changes */
const REDUCED_MOTION_VARIANTS: Variants = {
  initial: {},
  animate: {},
  exit: {},
  hidden: {},
  visible: {},
};

/** Instant transition for reduced motion */
const REDUCED_MOTION_TRANSITION: Transition = {
  duration: 0,
};

/**
 * Get animation props that respect reduced motion preference.
 * Returns empty variants when reduced motion is preferred.
 *
 * @param preset - Animation preset from ANIMATIONS object
 * @param shouldReduceMotion - Result of useReducedMotion() hook
 * @returns Props to spread on motion component
 */
export function getAnimationProps(
  preset: { variants: Variants; transition: Transition },
  shouldReduceMotion: boolean | null
) {
  if (shouldReduceMotion) {
    return {
      variants: REDUCED_MOTION_VARIANTS,
      transition: REDUCED_MOTION_TRANSITION,
    };
  }
  return {
    variants: preset.variants,
    transition: preset.transition,
  };
}

